import React, { useState, useEffect, useCallback } from 'react';
import { apiService, AccountBalance, DashboardStats } from '../services/api';
import Loader from './Loader';
import EmptyState from './EmptyState';
import { useNotifications } from './NotificationSystem';
import { handleApiError, trimFormData } from '../utils/errorHandler';
import './CashManagement.css';

interface AccountFormData {
  name: string;
  accountType: string;
  description: string;
  openingBalance: number;
}

const CashManagement: React.FC = () => {
  const [accounts, setAccounts] = useState<AccountBalance[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [showBalanceAdjustment, setShowBalanceAdjustment] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<AccountBalance | null>(null);
  const [adjustmentAmount, setAdjustmentAmount] = useState<number>(0);
  const [adjustmentReason, setAdjustmentReason] = useState<string>('');
  const { showSuccess, showError } = useNotifications();
  
  const [formData, setFormData] = useState<AccountFormData>({
    name: '',
    accountType: 'Bank',
    description: '',
    openingBalance: 0
  });

  const fetchData = useCallback(async () => {
    // CRITICAL: Don't make API calls if not authenticated
    if (!apiService.isAuthenticated()) {
      console.warn('CashManagement: Not authenticated, skipping API calls');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const [statsData, accountsData] = await Promise.all([
        apiService.getDashboardStats(),
        apiService.getAccounts()
      ]);
      
      setStats(statsData);
      setAccounts(accountsData);
    } catch (error: any) {
      // If 401, the global handler will redirect - don't show error
      if (error.status === 401 || error.errorCode === 'UNAUTHORIZED') {
        console.log('CashManagement: 401 detected, redirecting to login');
        return;
      }
      const appError = handleApiError(error);
      showError('Loading Error', appError.message);
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const trimmedData = trimFormData(formData);
      
      if (!trimmedData.name.trim()) {
        showError('Validation Error', 'Account name is required');
        return;
      }
      
      // Create account via API
      await apiService.createAccount({
        name: trimmedData.name,
        accountType: trimmedData.accountType,
        description: trimmedData.description || undefined,
        openingBalance: trimmedData.openingBalance
      });
      
      // Refresh accounts list to get updated data
      await fetchData();
      
      setFormData({
        name: '',
        accountType: 'Bank',
        description: '',
        openingBalance: 0
      });
      setShowAddAccount(false);
      showSuccess('Account Created', 'Account created successfully');
    } catch (error) {
      const appError = handleApiError(error);
      showError('Create Account Error', appError.message);
    }
  };

  const handleBalanceAdjustment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAccount) return;

    try {
      // For now, we'll update the local state since we don't have an API endpoint yet
      const updatedAccounts = accounts.map(account => 
        account.id === selectedAccount.id 
          ? { ...account, balance: account.balance + adjustmentAmount }
          : account
      );
      
      setAccounts(updatedAccounts);
      setAdjustmentAmount(0);
      setAdjustmentReason('');
      setSelectedAccount(null);
      setShowBalanceAdjustment(false);
    } catch (error) {
      console.error('Error adjusting balance:', error);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const getAccountTypeColor = (type: string) => {
    switch (type) {
      case 'Bank': return '#4299e1';
      case 'Cash': return '#48bb78';
      case 'ODCC': return '#ed8936';
      case 'Loan': return '#f56565';
      default: return '#718096';
    }
  };

  const getAccountTypeIcon = (type: string) => {
    switch (type) {
      case 'Bank': return '🏦';
      case 'Cash': return '💰';
      case 'ODCC': return '💳';
      case 'Loan': return '📉';
      default: return '🏦';
    }
  };

  if (loading) {
    return (
      <div className="cash-management">
        <Loader message="Loading cash management data..." size="large" />
      </div>
    );
  }

  return (
    <div className="cash-management">
      <div className="cash-header">
        <h1>Cash Management</h1>
        <p>Manage your accounts and track cash flow</p>
        <div className="header-actions">
          <button 
            className="btn-primary"
            onClick={() => setShowAddAccount(true)}
          >
            <span>+</span>
            Add Account
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card total-balance">
          <div className="card-icon">💰</div>
          <div className="card-content">
            <h3>Total Balance</h3>
            <p>{formatCurrency(stats?.totalBalance || 0)}</p>
          </div>
        </div>
        
        <div className="summary-card total-accounts">
          <div className="card-icon">🏦</div>
          <div className="card-content">
            <h3>Total Accounts</h3>
            <p>{accounts.length}</p>
          </div>
        </div>
        
        <div className="summary-card monthly-income">
          <div className="card-icon">📈</div>
          <div className="card-content">
            <h3>Monthly Income</h3>
            <p>{formatCurrency(stats?.monthlyIncome || 0)}</p>
          </div>
        </div>
        
        <div className="summary-card monthly-expenses">
          <div className="card-icon">📉</div>
          <div className="card-content">
            <h3>Monthly Expenses</h3>
            <p>{formatCurrency(stats?.monthlyExpenses || 0)}</p>
          </div>
        </div>
      </div>

      {/* Accounts Overview */}
      <div className="accounts-section">
        <div className="section-header">
          <h2>Account Overview</h2>
          <p>Manage your financial accounts and balances</p>
        </div>
        
        <div className="accounts-grid">
          {accounts.length === 0 ? (
            <EmptyState
              icon="🏦"
              title="No accounts yet"
              message="Create your first account to start managing your cash flow."
              actionLabel="Add Account"
              onAction={() => setShowAddAccount(true)}
            />
          ) : (
            accounts.map((account) => (
            <div key={account.id} className="account-card">
              <div className="account-header">
                <div className="account-icon" style={{ backgroundColor: getAccountTypeColor(account.accountType) }}>
                  {getAccountTypeIcon(account.accountType)}
                </div>
                <div className="account-info">
                  <h3>{account.name}</h3>
                  <p>{account.accountType}</p>
                </div>
                <div className="account-actions">
                  <button 
                    className="adjust-btn"
                    onClick={() => {
                      setSelectedAccount(account);
                      setShowBalanceAdjustment(true);
                    }}
                    title="Adjust Balance"
                  >
                    ⚖️
                  </button>
                </div>
              </div>
              
              <div className="account-balance">
                <span className={`balance-amount ${account.balance >= 0 ? 'positive' : 'negative'}`}>
                  {formatCurrency(account.balance)}
                </span>
              </div>
              
              <div className="account-details">
                <div className="detail-item">
                  <span>Account ID:</span>
                  <span>{account.id}</span>
                </div>
                <div className="detail-item">
                  <span>Type:</span>
                  <span>{account.accountType}</span>
                </div>
              </div>
            </div>
            ))
          )}
        </div>
      </div>

      {/* Add Account Modal */}
      {showAddAccount && (
        <div className="modal-overlay">
          <div className="modal account-form">
            <div className="form-header">
              <h2>Add New Account</h2>
              <button 
                className="close-btn" 
                onClick={() => setShowAddAccount(false)}
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleAddAccount}>
              <div className="form-group">
                <label>Account Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Savings Account, Credit Card"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Account Type</label>
                <select
                  value={formData.accountType}
                  onChange={(e) => setFormData({ ...formData, accountType: e.target.value })}
                  required
                >
                  <option value="Bank">Bank</option>
                  <option value="Cash">Cash</option>
                  <option value="ODCC">ODCC</option>
                  <option value="Loan">Loan</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Description (Optional)</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Account description or notes"
                  rows={3}
                />
              </div>
              
              <div className="form-group">
                <label>Opening Balance</label>
                <input
                  type="number"
                  value={formData.openingBalance}
                  onChange={(e) => setFormData({ ...formData, openingBalance: parseFloat(e.target.value) || 0 })}
                  placeholder="0.00"
                  step="0.01"
                />
              </div>
              
              <div className="form-actions">
                <button type="button" onClick={() => setShowAddAccount(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Add Account
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Balance Adjustment Modal */}
      {showBalanceAdjustment && selectedAccount && (
        <div className="modal-overlay">
          <div className="modal balance-adjustment">
            <div className="form-header">
              <h2>Adjust Balance - {selectedAccount.name}</h2>
              <button 
                className="close-btn" 
                onClick={() => setShowBalanceAdjustment(false)}
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleBalanceAdjustment}>
              <div className="current-balance">
                <h3>Current Balance</h3>
                <p className="balance-display">{formatCurrency(selectedAccount.balance)}</p>
              </div>
              
              <div className="form-group">
                <label>Adjustment Amount</label>
                <input
                  type="number"
                  value={adjustmentAmount}
                  onChange={(e) => setAdjustmentAmount(parseFloat(e.target.value) || 0)}
                  placeholder="Enter positive or negative amount"
                  step="0.01"
                  required
                />
                <small>Use positive for increase, negative for decrease</small>
              </div>
              
              <div className="form-group">
                <label>Reason for Adjustment</label>
                <input
                  type="text"
                  value={adjustmentReason}
                  onChange={(e) => setAdjustmentReason(e.target.value)}
                  placeholder="e.g., Bank reconciliation, Error correction"
                  required
                />
              </div>
              
              <div className="new-balance">
                <h3>New Balance</h3>
                <p className="balance-display">
                  {formatCurrency(selectedAccount.balance + adjustmentAmount)}
                </p>
              </div>
              
              <div className="form-actions">
                <button type="button" onClick={() => setShowBalanceAdjustment(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Adjust Balance
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CashManagement;